import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Typography, type TypographyProps } from '@heroui/react';
import type { CatalogImportRecord } from '@/api/models';
import type { CatalogImportMode } from '@/pages/catalogImport/store';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    ImportStatsCard,
    UndoImportAction,
    UndoImportDialog,
    importPostingOf,
} from './components';
import type { UseResultStepResult } from './useResultStep';

type ResultStepViewProps = Omit<UseResultStepResult, 'record'> & {
    record: CatalogImportRecord;
    /** Offers Undo (the wizard); the in-shell page does not. */
    showUndo: boolean;
    /** The way on reads Next step in the wizard and Back to Branches on the in-shell page. */
    mode: CatalogImportMode;
};

// The title is the step's only page-level heading.
const asH1: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <h1 {...domProps}>{children}</h1>;

/**
 * Result: the title, the import's stats card with its posting line (and
 * the products it imported as drafts, when it drafted any), and the ways
 * on: **Next step** (primary; **Back to Branches** on the in-shell page,
 * `mode`) and **Import another file** (secondary, before it), and, when
 * `showUndo`, **Undo this import** on the other side with its helper and
 * its confirm (bos-app#221: moving on is the main path). On a phone
 * (`isNarrow`) the figures stack, and the buttons take the full width, the
 * primary one on top and Undo last.
 */
export const ResultStepView: React.FC<ResultStepViewProps> = ({
    record,
    showUndo,
    mode,
    onImportAnother,
    onNext,
    isNarrow,
    isUndoOpen,
    onUndo,
    onCancelUndo,
    onConfirmUndo,
    isUndoing,
    undoFailure,
}) => {
    const { t } = useTranslation('pages');
    const { posted } = importPostingOf(record);

    return (
        <section
            className="flex flex-col gap-8 py-4 sm:py-8"
            {...getNodeTestIdProp('catalog-import-result')}
        >
            <div className="flex flex-col gap-2">
                <Typography
                    render={asH1}
                    type="h2"
                    {...getNodeTestIdProp('catalog-import-result-title')}
                >
                    {t('catalogImport.result.title')}
                </Typography>
                <Typography.Paragraph color="muted">
                    {t('catalogImport.result.intro')}
                </Typography.Paragraph>
            </div>

            <ImportStatsCard isNarrow={isNarrow} record={record} />

            <div className="flex flex-col-reverse gap-6 sm:flex-row sm:items-start">
                {showUndo ? (
                    <UndoImportAction onUndo={onUndo} posted={posted} />
                ) : null}
                <div className="flex flex-col-reverse gap-3 sm:ms-auto sm:flex-row">
                    <Button
                        className="w-full sm:w-auto"
                        onPress={onImportAnother}
                        variant="secondary"
                        {...getNodeTestIdProp(
                            'catalog-import-result-import-another',
                        )}
                    >
                        {t('catalogImport.result.importAnother')}
                    </Button>
                    <Button
                        className="w-full sm:w-auto"
                        onPress={onNext}
                        variant="primary"
                        {...getNodeTestIdProp('catalog-import-result-next')}
                    >
                        {mode === 'shell'
                            ? t('catalogImport.result.backToBranches')
                            : t('catalogImport.result.next')}
                    </Button>
                </div>
            </div>

            {showUndo ? (
                <UndoImportDialog
                    branchName={record.branchName}
                    failure={undoFailure}
                    isOpen={isUndoOpen}
                    isUndoing={isUndoing}
                    onCancel={onCancelUndo}
                    onConfirm={onConfirmUndo}
                    posted={posted}
                />
            ) : null}
        </section>
    );
};
